import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';

const ROLE_OPTIONS = [
  { code: 'role-acct-mgmt', label: 'Account Management' },
  { code: 'role-key-mgmt', label: 'Key Management' },
  { code: 'role-support', label: 'Support' },
];

const STATUS_OPTIONS = ['All Statuses', 'Pending', 'Accepted', 'Rejected', 'Expired'];

const OpsOrgInvites = () => {
  const { orgId } = useParams();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchInvites = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/invites`, {
        params: {
          search: searchTerm.trim() || undefined,
          status: statusFilter,
          page,
          page_size: 100,
        },
      });
      setInvites(response.data?.invites || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load invite history.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) fetchInvites();
  }, [orgId, page]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (orgId) fetchInvites();
  }, [orgId, searchTerm, statusFilter]);

  const totalPages = Math.max(Math.ceil(total / 100), 1);

  const mappedInvites = useMemo(() => invites.map((invite) => ({
    ...invite,
    roleLabels: (invite.roles || []).map((role) => ROLE_OPTIONS.find((r) => r.code === role)?.label || role),
  })), [invites]);

  return (
    <div className="card">
      <div className="card-body">
        <h5 className="card-title">Invite History</h5>
        {status.message && (
          <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>
        )}
        <div className="row g-3 align-items-end mb-3">
          <div className="col-md-7">
            <label className="form-label">Search</label>
            <input
              className="form-control"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search email, role, or status"
            />
          </div>
          <div className="col-md-5">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
        {loading ? (
          <div className="text-muted">Loading invite history...</div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {mappedInvites.map((invite) => (
                  <tr key={invite.id}>
                    <td>{invite.email}</td>
                    <td>
                      {invite.roleLabels.length === 0 ? (
                        <span className="text-muted">None</span>
                      ) : (
                        invite.roleLabels.map((label) => (
                          <span key={label} className="badge bg-light text-dark border me-1">
                            {label}
                          </span>
                        ))
                      )}
                    </td>
                    <td>
                      <span className={`badge ${
                        invite.status === 'Pending'
                          ? 'bg-warning text-dark'
                          : invite.status === 'Accepted'
                            ? 'bg-success'
                            : invite.status === 'Rejected'
                              ? 'bg-secondary'
                              : 'bg-light text-dark'
                      }`}>
                        {invite.status}
                      </span>
                    </td>
                    <td>{invite.created_at ? new Date(invite.created_at).toLocaleDateString() : '—'}</td>
                    <td>{invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
                {mappedInvites.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-muted text-center">No invites match your filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted small">
            {total === 0
              ? 'Showing 0 of 0'
              : `Showing ${(page - 1) * 100 + 1}–${Math.min(page * 100, total)} of ${total}`}
          </div>
          <div className="btn-group">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsOrgInvites;
